import { extractApiFormError } from "@/api/utils/error";
import { mapProfileSkillWriteError } from "@/api/utils/profile-skill-write-error";
import type { SectorValue } from "../context/OnboardingContext";

/**
 * The fields of a `400` from `POST /profile/onboarding` that belong to the
 * «Settore» card: `profile` (the backend's own refusal of a sector or an area,
 * AC21) and the three taxonomy fields of the body.
 */
const SECTOR_FIELDS = [
  "profile",
  "job_category",
  "job_category_secondary",
  "job_subcategories",
] as const;

/**
 * The message the backend wrote for the «Settore» card, or `null` when the
 * failure is anything else (another field, a `5xx`, no response at all): those
 * keep today's toast. Only a validation `400` can answer, and only the text the
 * backend wrote, never axios' own.
 */
export function taxonomyReviewError(error: unknown): string | null {
  const mapped = mapProfileSkillWriteError(error);
  if (mapped.kind !== "validation") return null;

  const { fieldErrors } = extractApiFormError<string>(error);
  if (!fieldErrors) return null;

  for (const field of SECTOR_FIELDS) {
    const message = fieldErrors[field]?.find(
      (candidate) => typeof candidate === "string" && candidate.length > 0,
    );
    if (message) return message;
  }
  return null;
}

/**
 * What the body sends of a sector, as one string: the three keys and nothing
 * else (origins never travel, D6). Two values with the same signature would
 * send the same request, so a refusal of one still holds for the other.
 */
export function sectorSignature(value: SectorValue): string {
  return JSON.stringify([
    value.job_category,
    value.job_category_secondary,
    value.job_subcategories.map((subcategory) => subcategory.key).sort(),
  ]);
}
